import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, Loader2, X, AlertCircle } from 'lucide-react';
import { Input } from '@/components/shared';

/**
 * Controlled LocationInput Component
 * Parent manages value, suggestions, and API logic
 *
 * @param {Object} props
 * @param {string} props.value - Controlled input value
 * @param {Function} props.onChange - Called when user selects a location
 * @param {Function} props.onInputChange - Called when input text changes
 * @param {boolean} props.isLoading - Whether suggestions are being fetched
 * @param {Array} props.suggestions - Array of suggestion objects [{ id, text, place_name, ... }]
 * @param {string} props.placeholder - Input placeholder
 * @param {string} props.error - Error message
 * @param {boolean} props.disabled - Disable input
 * @param {string} props.className - Extra class names
 * @param {string} props.name - Input name
 * @param {string} props.id - Input id
 * @param {boolean} props.required - Mark input as required
 */
export const LocationInput = ({
  value = '',
  onChange,
  onInputChange,
  isLoading = false,
  suggestions = [],
  placeholder = 'Search for a location...',
  error,
  disabled = false,
  className = '',
  name = 'location',
  id,
  required = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  handleSelectedClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle input change
  const handleInputChange = e => {
    const newValue = e.target.value;
    onInputChange?.(newValue);
    setIsOpen(true);
  };

  // Handle selection
  const handleSelect = suggestion => {
    const locationData = {
      id: suggestion.id,
      placeName: suggestion.place_name,
      text: suggestion.text,
      coordinates: {
        latitude: suggestion.center[1],
        longitude: suggestion.center[0],
      },
      placeType: suggestion.place_type,
      context: suggestion.context,
      bbox: suggestion.bbox,
      properties: suggestion.properties,
    };
    setIsOpen(false);
    setHighlightedIndex(-1);

    if (onChange) {
      onChange(locationData);
    }
  };

  // Handle clear
  const handleClear = () => {
    onInputChange?.('');
    handleSelectedClear();
    setIsOpen(false);
    inputRef.current?.focus();
  };

  // Keyboard navigation
  const handleKeyDown = e => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) handleSelect(suggestions[highlightedIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const showClearButton = value && !disabled;
  const hasError = !!error;

  return (
    <div className={`w-full ${className}`}>
      <div className="relative">
        <Search
          className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${
            disabled ? 'text-muted-foreground' : 'text-foreground/60'
          }`}
        />

        <Input
          ref={inputRef}
          id={id}
          name={name}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-label={ariaLabel || 'Location search'}
          aria-describedby={ariaDescribedby || (hasError ? `${name}-error` : undefined)}
          aria-autocomplete="list"
          aria-controls={isOpen ? `${name}-listbox` : undefined}
          aria-expanded={isOpen}
          className={`w-full pl-10 ${showClearButton ? 'pr-10' : 'pr-4'} py-2.5 border rounded-lg transition-all outline-none ${
            disabled ? 'cursor-not-allowed' : ''
          } focus:ring-2 ${
            hasError
              ? 'focus:ring-[var(--color-border-error)] focus:border-[var(--color-border-error)]'
              : 'focus:ring-[var(--color-border-focusRing)] focus:border-[var(--color-border-interactive)]'
          }`}
          autoComplete="off"
        />

        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-icon-muted" />}

          {showClearButton && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="transition-colors text-icon-muted hover:text-icon-muted"
              aria-label="Clear input"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && suggestions.length > 0 && (
          <ul
            ref={dropdownRef}
            id={`${name}-listbox`}
            role="listbox"
            className="absolute z-50 w-full mt-2 bg-background border rounded-lg shadow-lg max-h-64 overflow-y-auto"
          >
            {suggestions.map((s, index) => (
              <li
                key={s.id || index}
                role="option"
                aria-selected={index === highlightedIndex}
                onClick={() => handleSelect(s)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`px-4 py-3 cursor-pointer border-b last:border-b-0 hover:bg-subtle transition-colors ${
                  index === highlightedIndex ? 'bg-interactive-muted' : 'bg'
                }`}
              >
                <div className="flex items-start gap-3">
                  <MapPin
                    className="w-5 h-5 mt-0.5 flex-shrink-0"
                    style={{
                      color:
                        index === highlightedIndex ? 'var(--color-icon-interactive)' : 'var(--color-icon-interactive)',
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text)' }}>
                      {s.text}
                    </p>
                    <p className="text-xs truncate mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                      {s.place_name}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Error message */}
        {hasError && (
          <div
            id={`${name}-error`}
            role="alert"
            className="flex items-start gap-2 mt-2 text-sm"
            style={{ color: 'var(--color-text-error)' }}
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-icon-error)' }} />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};
